/** @format */
import { css } from 'styled-components';

import { CustomSelectAlignment, StyledCustomSelectProps } from './types';

/**
 * Base styles for the CustomSelect component
 *
 * @param {StyledCustomSelectProps} props the custom select props
 * @param {CustomSelectAlignment} props.alignment How the options should align to the select
 */
/* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
const baseStyles = ({ alignment }: StyledCustomSelectProps) => css`
    position: relative;
    display: block;
    background: none;
    width: 100%;
    border: 1px solid var(--colors-border-secondary);
    border-radius: 0.6rem;
    font-size: 1.2rem;
    font-weight: 300;
    text-align: left;
    padding: 0.9rem;
    color: var(--colors-text-secondary);
    cursor: pointer;
    background: var(--colors-surface-primary);

    &:active,
    &:focus {
        outline: var(--effect-border-lg) solid var(--effect-focus-ring);
    }

    svg {
        position: absolute;
        right: 0.8rem;
        top: 1.2rem;
        transform: rotate(90deg);
    }

    & ~ .options {
        display: flex;
        flex-direction: column;
        z-index: 100;
        position: absolute;
        top: calc(100% + 1.2rem);
        left: ${alignment === CustomSelectAlignment.LEFT ? '1.2rem' : 'auto'};
        right: ${alignment === CustomSelectAlignment.RIGHT ? '1.2rem' : 'auto'};
        width: 35rem;
        max-width: 35rem;
        max-height: 48rem;
        background-color: white;
        box-shadow: 0 0.4rem 1.2rem 0 var(--effect-focus-ring);
        border-radius: 1.2rem;

        &:before {
            content: '';
            width: 1rem;
            height: 1rem;
            position: absolute;
            top: -0.5rem;
            left: ${
                alignment === CustomSelectAlignment.LEFT ? '2.4rem' : 'auto'
            };
            right: ${
                alignment === CustomSelectAlignment.RIGHT ? '2.4rem' : 'auto'
            };
            background: white;
            transform-origin: 0.5rem 0.5rem;
            transform: rotate(45deg);
        }

        /* Used for working out which side the options should display */
        &.open-left {
            left: 1.2rem;
            right: auto;
        }
    }
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

/**
 * Get the styles for when the custom select is open or active
 *
 * @param {StyledCustomSelectProps} props the custom select props
 * @param {boolean} props.isOpen whether the options panel is visible
 * @param {boolean} props.isActive whether the custom select is active
 * */
function activeStyles({ isOpen, isActive }: StyledCustomSelectProps) {
    if (!isOpen && !isActive) {
        return css``;
    }

    /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
    return css`
        opacity: 1;
        border-width: 2px;
        padding: 0.8rem;

        svg {
            right: 0.7rem;
            top: 1.1rem;
        }
    `;
    /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
}

/**
 * The CustomSelect component styles
 *
 * @param {StyledCustomSelectProps} props The custom-select properties object
 */
export function styles(props: StyledCustomSelectProps) {
    return css`
        ${baseStyles(props)}
        ${activeStyles(props)}
    `;
}
